import { AutoMode } from '@mui/icons-material';
import { Alert, styled, Typography } from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import { closeSnackbar, useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { AppDispatch } from 'redux/store';

import { FeedbackContext } from '../../constants/feedbackConstants';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { useProgrammaticScroll } from '../../hooks/useProgrammaticScroll';
import { useSummaryFiles } from '../../hooks/useSummaryFiles';
import {
  getConversation,
  getMessages,
  updateFeedback,
} from '../../redux/actions/conversationActions';
import { getFolder } from '../../redux/actions/folderActions';
import { findPrompt } from '../../redux/actions/promptActions';
import { selectAppStatus, setCurrentApp } from '../../redux/reducers/appStatusReducer';
import {
  selectConversations,
  setCurrentConversation,
  setCurrentMessages,
  setCurrentNumTokens,
  setIsLoading,
  setIsScrolling,
} from '../../redux/reducers/conversationReducer';
import { selectFolders, setCurrentFolder } from '../../redux/reducers/folderReducer';
import { StyledChatContainer } from '../../theme/CustomComponents';
import { Conversation, Message } from '../../Types/conversation';
import { ProjectWithLastScanned } from '../../Types/dealGPT';
import { AppEnum, MessageFrom } from '../../Types/enums';
import { FeedbackInterface } from '../../Types/feedback';
import { Folder } from '../../Types/folder';
import { Prompt } from '../../Types/prompt';
import Feedback from '../Feedback';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';
import { useChat } from '../Providers/ChatProvider';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import OutputWarning from './OutputWarning';
import SummaryIntroChatBubble from './SummaryIntroChatBubble';

interface ChatProps {
  project?: ProjectWithLastScanned;
  isTemporary?: boolean;
}

const TemporaryStyledAlert = styled(Alert)({
  marginBottom: '10px',
  marginTop: '15px',
  backgroundColor: 'black',
  color: 'white',
  '.MuiAlert-icon': { display: 'flex', alignItems: 'center' },
});

const ChatContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  justifyContent: 'space-between',
  height: '100%',
});

const ChatContentContainer = styled('div')<{ isIAChat: boolean }>(({ isIAChat }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: isIAChat ? '100%' : undefined,
  margin: isIAChat ? '0px auto' : undefined,
}));

const PromptHeader = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const Chat = ({ project = undefined, isTemporary = false }: ChatProps): JSX.Element => {
  const analytics = useAnalytics();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { enqueueSnackbar } = useSnackbar();

  const { conversationId } = useParams();

  const { selectedApp, llm } = useSelector(selectAppStatus);

  const isIAChat = selectedApp === AppEnum.DEAL_GPT && !!project;

  const { isAdmin } = useAuth(); //NIGEL
  const { currentFolder } = useSelector(selectFolders); //NIGEL

  const { currentConversation, currentMessages, isLoading, isScrolling } =
    useSelector(selectConversations);

  const {
    editor,
    inputRef,
    setEditorPrompt,
    setTools,
    setUploadedFiles,
    setShowNormalTextField,
    handleSubmit,
    isNavigatingToNewConversationRef,
    temporaryMessages,
    setTemporaryMessages,
    clearChatState,
  } = useChat();

  const { messageContainerRef, isProgrammaticScrollRef } = useProgrammaticScroll({
    isLoading,
    isScrolling,
  });

  const [prompt, setPrompt] = useState<Prompt>();
  const [promptId, setPromptId] = useState<string>();
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(true);
  const [shouldShowTempChatInfo, setShouldShowTempChatInfo] = useState<boolean>(true);
  const snackbar60Messages = useRef<string | number>();

  const { summaryFiles, summaryCollections, activeTab, totalDocs, scopePagePairs } =
    useSummaryFiles({
      projectId: project?.id,
      summaryFileIds: currentConversation?.summaryFileIds,
    });

  // Use temporary messages when in temporary mode, otherwise use Redux messages
  const displayMessages = isTemporary ? temporaryMessages : currentMessages;

  // Helper function to update token count from messages
  const updateTokenCount = useCallback(
    (messages: Message[]) => {
      const mostRecentAIMessages = messages.filter((message) => message.from === MessageFrom.AI);
      dispatch(
        setCurrentNumTokens(
          mostRecentAIMessages.length > 0
            ? mostRecentAIMessages[mostRecentAIMessages.length - 1].tokens
            : 0
        )
      );
    },
    [dispatch]
  );

  const highlightWords = useCallback((text: string, wordsToHighlight: string[]) => {
    // Escape asterisks for regex
    const escapeAsterisks = (str: string) => str.replace(/\*/g, '\\*');
    let highlightedText = text;
    wordsToHighlight.forEach((word) => {
      const regex = new RegExp(`\\b${escapeAsterisks(word)}\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, (matched) => {
        return `<mark style="background-color: thistle;"><span style="color: blue; font-weight: bold;">${
          '&nbsp;' + matched + '&nbsp'
        }</span></mark>`;
      });
    });

    return highlightedText + '&nbsp;';
  }, []);

  const handleFeedbackSubmit = useCallback(
    (feedback: FeedbackInterface, messageId: string) => {
      const appAnalytic = isIAChat
        ? AnalyticsEvent.WM_IA_CHAT_FEEDBACK
        : AnalyticsEvent.WM_NIGEL_CHAT_FEEDBACK;

      analytics.event(
        appAnalytic,
        {
          id: messageId,
          value: `Feedback=${feedback.thumbsUp}`,
        },
        {
          projectId: project?.id ?? undefined,
          messageId,
          thumbsUp: feedback.thumbsUp ?? undefined,
          otherFeedback: feedback.otherFeedback ?? undefined,
          suggestedFeedback: feedback.suggestedFeedback.toString() ?? undefined,
        }
      );

      dispatch(updateFeedback({ messageId, messageFeedback: feedback }));
    },
    [isIAChat, analytics, dispatch, project?.id]
  );

  //triggers when the prompt id changes in URL
  useEffect(() => {
    const newPromptId = searchParams.get('prompt');
    if (newPromptId) {
      dispatch(setIsLoading(true));
      setPromptId(newPromptId);
      searchParams.delete('prompt');
      setSearchParams(searchParams);
      setPrompt(undefined);
    }
  }, [searchParams, setSearchParams, dispatch, setUploadedFiles]);

  //fetches the prompt had been found in URL
  useEffect(() => {
    if (promptId && !prompt) {
      const fetchPrompt = () => {
        dispatch(findPrompt(promptId)).then((action: PayloadAction<Prompt | unknown>) => {
          const newPrompt = action.payload as Prompt;
          const starterFiles = newPrompt?.files?.map((file) => ({
            ...file,
            isStarterFile: true,
          }));

          setPrompt(newPrompt);
          setPromptId(undefined);
          setUploadedFiles(starterFiles ?? []);
          setEditorPrompt(newPrompt); //Sets Prompt to ChatProvider and updates the Chat input editor text/files
          setTools(newPrompt.tools ?? []);
          dispatch(setIsLoading(false));
          //if auto submitting a prompt and outside the library drawer - submits right away
          if (!newPrompt.userEditRequired && !conversationId) {
            handleSubmit({
              conversationId,
              isPrivate: false,
              isTemporaryChat: isTemporary,
              toolsOverride: newPrompt.tools,
            });
          } else {
            dispatch(setIsLoading(false));
            const highlightedPrompt = highlightWords(
              newPrompt.prompt,
              newPrompt.variables
            ).replaceAll('\n', '</br>');
            editor?.commands.setContent(highlightedPrompt);
            setShowNormalTextField(false);
          }
        });
      };
      fetchPrompt();
    }
  }, [
    prompt,
    promptId,
    editor,
    conversationId,
    isIAChat,
    isTemporary,
    highlightWords,
    handleSubmit,
    navigate,
    dispatch,
    setEditorPrompt,
    setShowNormalTextField,
    setTools,
    setUploadedFiles,
  ]);

  //triggers when a different conversation is selected
  useEffect(() => {
    // Skip loading conversation in temporary mode
    if (isTemporary) return;

    if (currentConversation?.id === conversationId) return;

    const loadConversation = async () => {
      if (!conversationId) {
        clearChatState();
        return;
      }

      try {
        // Only clear the input if we're not navigating to a newly created conversation from streaming
        // This preserves user input during automatic navigation after message submission
        if (!isNavigatingToNewConversationRef.current) {
          setEditorPrompt(undefined);
        } else {
          // Reset the flag after checking it
          isNavigatingToNewConversationRef.current = false;
        }
        dispatch(setIsLoading(true));
        const conversationAction = await dispatch(getConversation(conversationId));
        const conversationPayload = conversationAction.payload;

        if (conversationPayload === 'Failed to fetch conversation') {
          enqueueSnackbar('Conversation not found', { variant: 'error' });
          navigate(`/folder/${currentFolder?.id}`);
          return;
        }

        const newConversation = conversationPayload as Conversation;
        dispatch(setCurrentConversation(newConversation));

        let newTools = newConversation.tools ?? [];
        setTools(newTools);
        newTools = Array.from(new Set(newTools.concat(newConversation?.prompt?.tools ?? [])));

        setUploadedFiles([]);
        dispatch(setCurrentApp(newConversation.appName));

        if (newConversation.folderId && newConversation.folderId !== currentFolder?.id) {
          const folderAction = await dispatch(
            getFolder({ folderId: newConversation.folderId, isAdmin })
          );
          dispatch(setCurrentFolder(folderAction.payload as Folder));
        }

        const messagesAction = await dispatch(getMessages(conversationId));
        const newMessages = (
          Array.isArray(messagesAction.payload) ? messagesAction.payload : []
        ) as Message[];

        if (newConversation.prompt && newConversation.prompt.userEditRequired === false) {
          if (newMessages.length > 0) {
            newMessages.shift();
          }
        }

        if (newMessages.length >= 60 && snackbarOpen) {
          snackbar60Messages.current = enqueueSnackbar(
            'Heads up! This conversation is getting long—starting a new one can help things run smoothly.',
            {
              variant: 'warning',
              autoHideDuration: null,
              preventDuplicate: true,
              onClose: () => setSnackbarOpen(false),
            }
          );
        }
        dispatch(setCurrentMessages(newMessages));
        setSnackbarOpen(true);

        updateTokenCount(newMessages);
      } catch (e) {
        console.log(e);
        enqueueSnackbar('Error fetching conversation', { variant: 'error' });
        dispatch(setCurrentConversation(undefined));
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    loadConversation();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversationId,
    navigate,
    currentConversation?.id,
    enqueueSnackbar,
    inputRef,
    setTools,
    setUploadedFiles,
    dispatch,
    snackbarOpen,
    currentFolder?.id,
    isAdmin,
    isIAChat,
    setEditorPrompt,
    isTemporary,
  ]);

  // clear the current conversation on unmount
  useEffect(() => {
    return () => {
      dispatch(setCurrentMessages([]));
      dispatch(setCurrentNumTokens(0));
      clearChatState();
      setPrompt(undefined);
    };
  }, [dispatch, clearChatState, isTemporary]);

  //handles autoscroll to bottom of chat when new messages are added
  useEffect(() => {
    const handleUserScroll = () => {
      // ignore scroll events that were caused by our programmatic scrolling
      if (isProgrammaticScrollRef.current) return;

      const container = messageContainerRef.current;
      if (!container) return;

      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollHeight - scrollTop > clientHeight) {
        dispatch(setIsScrolling(true));
      } else {
        dispatch(setIsScrolling(false));
      }
    };

    const container = messageContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleUserScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleUserScroll);
      }
    };
  }, [dispatch, messageContainerRef, isProgrammaticScrollRef, selectedApp]);

  // Handle clear query param for temporary mode
  useEffect(() => {
    if (isTemporary && searchParams.get('clear') === 'true') {
      setTemporaryMessages([]);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('clear');
      setSearchParams(newParams);
    }
  }, [isTemporary, searchParams, setSearchParams, setTemporaryMessages]);

  // Update temporary token count based on messages
  useEffect(() => {
    if (!isTemporary) return;
    updateTokenCount(temporaryMessages);
  }, [isTemporary, temporaryMessages, currentMessages, updateTokenCount]);

  //clears conversation and files, and closes snack bar when leaving chat page
  useEffect(() => {
    return () => {
      if (snackbar60Messages.current) {
        closeSnackbar(snackbar60Messages.current);
      }

      setEditorPrompt(undefined);
      setUploadedFiles([]);
    };
  }, [setEditorPrompt, setUploadedFiles, isProgrammaticScrollRef, dispatch]);

  return (
    <ChatContainer>
      <StyledChatContainer
        ref={messageContainerRef}
        maxWidth="lg"
        style={{
          paddingLeft: '0px',
          paddingRight: '20px',
          height: isIAChat ? '70vh' : 'calc(100vh - 200px)',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <ChatContentContainer isIAChat={isIAChat}>
          {/* Private Chat Warning Banner */}
          {isTemporary && (
            <div style={{ position: 'sticky', zIndex: '999', marginBottom: '10px', top: '0' }}>
              <TemporaryStyledAlert icon={`🥸`}>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: '17px', fontWeight: 500, color: 'white' }}
                  >
                    {`This chat won't appear in your history. Your messages are not saved if you leave this page!`}
                  </Typography>
                </div>
              </TemporaryStyledAlert>
            </div>
          )}

          {/* Welcome Message for Temporary Mode */}
          {isTemporary && shouldShowTempChatInfo && (
            <>
              <Typography
                variant="h4"
                sx={{ marginBottom: '10px', fontWeight: '700', fontSize: '36px' }}
                align="left"
              >
                {`Welcome to Temporary Chat`}
                <br />
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: '16px' }} align="left">
                Temporary Chat allows you to chat with Nigel without having to worry about the
                conversation history being saved. This also means if you leave this page, you will
                lose the conversation! <br />
                Happy Chatting! 😊
              </Typography>
            </>
          )}

          {/* Auto submit Prompt Header Section */}
          {(prompt?.userEditRequired === false ||
            currentConversation?.prompt?.userEditRequired === false) && (
            <PromptHeader>
              <div style={{ width: '70%' }}>
                <AutoMode sx={{ width: '16px', height: '16px', marginY: '20px' }} />
                <Typography
                  variant="body1"
                  sx={{ marginTop: '-20px', marginBottom: '10px', fontWeight: 700 }}
                >
                  {(prompt || currentConversation?.prompt)?.title}
                </Typography>
                <Typography sx={{ fontSize: '14px', marginBottom: '20px' }}>
                  {(prompt || currentConversation?.prompt)?.description}
                </Typography>
              </div>
            </PromptHeader>
          )}
          {/* New Chat Placeholder */}
          {displayMessages.length === 0 &&
            !prompt?.id &&
            (!summaryFiles || summaryFiles.length === 0) && (
              <ChatBubble
                llm={llm}
                key="placeholder"
                message={{
                  from: MessageFrom.AI,
                  message:
                    'Start a new conversation with me by typing in the box or select an old conversation from the left! 😊',
                }}
              />
            )}
          {/* Summary Chat Intro message  */}
          {summaryFiles && summaryFiles.length > 0 && (
            <SummaryIntroChatBubble
              llm={llm}
              activeTab={activeTab}
              totalDocuments={totalDocs}
              scopePagePairs={scopePagePairs}
              collections={summaryCollections?.map((p) => p.name) ?? []}
            />
          )}
          {/* Prompt Edit Required Bubble */}
          {displayMessages.length === 0 && prompt?.userEditRequired && (
            <ChatBubble
              llm={llm}
              key="edit-required-bubble"
              message={{
                from: MessageFrom.AI,
                message: `Looks like you're using a prompt, make sure to edit it if necessary before sending.`,
              }}
            />
          )}
          {/* Message List */}
          {displayMessages.map((message: Message, index: number) => {
            if (
              message.from === MessageFrom.USER &&
              index > 0 &&
              (displayMessages[index - 1].mcpApprovalRequests?.length || 0) > 0
            ) {
              return null;
            }
            return <ChatBubble llm={llm} key={index} message={message} privateChat={isTemporary} />;
          })}
          {/* Feedback Component - Hide in temporary mode */}
          {!isTemporary &&
            displayMessages.length > 0 &&
            displayMessages[displayMessages.length - 1].from === MessageFrom.AI &&
            !isLoading && (
              <div style={{ display: 'flex', gap: 10 }}>
                <Feedback
                  initialFeedback={
                    displayMessages[displayMessages.length - 1].messageFeedback as
                      | FeedbackInterface
                      | undefined
                  }
                  context={FeedbackContext.MESSAGE}
                  onFeedbackSubmit={(feedback) =>
                    handleFeedbackSubmit(feedback, displayMessages[displayMessages.length - 1].id)
                  }
                />
                <OutputWarning />
              </div>
            )}
          {/* Loading State */}
          {isLoading &&
            ((displayMessages.length > 0 &&
              displayMessages[displayMessages.length - 1].from === MessageFrom.USER) ||
              displayMessages.length === 0) && (
              <ChatBubble llm={llm} key="loading" message={{ from: MessageFrom.AI }} />
            )}
        </ChatContentContainer>
      </StyledChatContainer>
      {/* Chat Input Section */}
      <StyledChatContainer maxWidth="lg" style={{ paddingLeft: '0px', paddingRight: '20px' }}>
        <ChatInput
          project={project}
          isTemporary={isTemporary}
          onTempMessageSent={() => setShouldShowTempChatInfo(false)}
        />
      </StyledChatContainer>
    </ChatContainer>
  );
};

export default Chat;
